import React, { useLayoutEffect, useState, useMemo } from 'react';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { Field } from 'formik';

export default function LocationForm({ values, errors, touched, handleChange, setFieldValue, stateRef }) {
  const [mapCenter, setMapCenter] = useState({
    lat: 14.5995,
    lng: 120.9842
  });

  useLayoutEffect(() => {
    if (values.markerPosition) {
      setMapCenter(values.markerPosition);
      stateRef.current.markerPosition = values.markerPosition;
    }
  }, [values.markerPosition, stateRef]);

  // Update form field when marker position changes
  React.useEffect(() => {
    if (values.markerPosition) {
      // This will trigger Formik's validation
      setFieldValue('markerPosition', values.markerPosition, true);
    }
  }, [values.markerPosition, setFieldValue]);

  const mapContainerStyle = useMemo(() => ({
    width: '100%',
    height: '400px',
    borderRadius: '8px',
    border: '1px solid #ccc'
  }), []);

  const mapOptions = useMemo(() => ({
    disableDefaultUI: true,
    zoomControl: true,
    mapTypeControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    rotateControl: false,
    styles: [
      {
        featureType: "poi",
        elementType: "labels",
        stylers: [{ visibility: "off" }]
      },
      {
        featureType: "transit",
        elementType: "labels",
        stylers: [{ visibility: "off" }]
      }
    ]
  }), []);

  return (
    <div className="form-section">
      <h3 className="section-title">Location</h3>
      <small className="text-500 block mb-2">
        Drag and drop the pin to set your exact shop location
      </small>

      <div className="map-container">
        <GoogleMap
          mapContainerStyle={mapContainerStyle}
          center={stateRef.current.markerPosition || mapCenter}
          zoom={15}
          options={mapOptions}
          onClick={(e) => {
            const newPosition = {
              lat: e.latLng.lat(),
              lng: e.latLng.lng()
            };
            stateRef.current.markerPosition = newPosition;
            setFieldValue('markerPosition', newPosition);
          }}
        >
          {stateRef.current.markerPosition && (
            <Marker
              position={stateRef.current.markerPosition}
              draggable={true}
              onDragEnd={(e) => {
                const newPosition = {
                  lat: e.latLng.lat(),
                  lng: e.latLng.lng()
                };
                stateRef.current.markerPosition = newPosition;
                setFieldValue('markerPosition', newPosition);
              }}
            />
          )}
        </GoogleMap>
      </div>

      {errors.markerPosition && touched.markerPosition && (
        <small className="p-error p-mt-2">{errors.markerPosition}</small>
      )}

      <Field name="storeAddress">
        {({ field, meta }) => (
          <div className="field">
            <label htmlFor="storeAddress">Address</label>
            <InputText
              id="storeAddress"
              {...field}
              onChange={(e) => {
                handleChange(e);
                stateRef.current.storeAddress = e.target.value;
              }}
              className={meta.touched && meta.error ? 'p-invalid' : ''}
              placeholder="Building, Floor, Unit, Street, City"
            />
            {meta.touched && meta.error ? (
              <small className="p-error">{meta.error}</small>
            ) : (
              <small className="text-500">
                Include specific details (building, floor, unit) to help customers find your store
              </small>
            )}
          </div>
        )}
      </Field>

      <Field name="directions">
        {({ field, meta }) => (
          <div className="field">
            <label htmlFor="directions">Directions & Landmarks</label>
            <InputTextarea
              id="directions"
              {...field}
              onChange={(e) => {
                handleChange(e);
                stateRef.current.directions = e.target.value;
              }}
              rows={3}
              maxLength={150}
              className={meta.touched && meta.error ? 'p-invalid' : ''}
              placeholder="Nearby landmarks or specific directions"
            />
            {meta.touched && meta.error ? (
              <small className="p-error">{meta.error}</small>
            ) : (
              <small className="text-500">
                Help customers find your store with nearby landmarks or specific directions
              </small>
            )}
          </div>
        )}
      </Field>
    </div>
  );
}
